import {
  trackReservationEvent,
  trackPurchaseEvent,
  gaTracker,
  ecTracker,
  trackEvent,
  serializePurchase as serializePurchaseForTracking,
} from 'user-analytics';
import { CONTINUE_WITH_SEATS, CHECKOUT_DETAILS_CLICKED } from 'constants/TrackEvents';
import { CurrentLang } from 'utils/lang';
import { getInstance } from 'utils/taplytics';
import { removeURLParam } from 'utils/queryString/urlsCleaner';
import { isAccountHolder } from 'utils/dotersFlexibleEditing';
import { buildZoneMap, getSeatZone } from 'utils/seatPricing';
import {
  ensureRnMixpanelIdentified,
  getRnMixpanelNativeEventProps,
} from 'utils/rnMixpanelNativeProps';
import { serializeLodgingCheckboxProps } from 'utils/lodgingCheckboxAnalytics';
import { serializeCdsFrequentPassengerOptInProps } from 'utils/cdsFrequentPassengerOptInAnalytics';
import store from '@/store';
import serializeReservation from './reservation';
import { getSegmentsOccupancy } from './segmentsOccupancy';

function serializeEcommerceReservation(reservation) {
  const { transportType, departure, origin, destination, fragments, tripSlug } = reservation;
  let transporterName = [];
  let typeName;
  let hasSeatSelection;
  let commissionAmount = 0;

  if (transportType === 'bus') {
    const { line } = fragments[0];

    transporterName.push(line.name);
    typeName = 'bus';
    hasSeatSelection = line.allowsSeatSelection;
    commissionAmount = (line.commissionEstimate / 100) * reservation.pricing.total;
  } else if (transportType === 'flight') {
    transporterName.push(fragments[0].legs.map((leg) => leg.carrier.name));
    typeName = 'flight';
    hasSeatSelection = false;
    commissionAmount = (fragments[0].carrier.commissionEstimate / 100) * reservation.pricing.total;
  } else {
    typeName = 'multicarrier';
    hasSeatSelection = false;

    fragments.forEach((fragment) => {
      if (fragment.transportType === 'flight') {
        const { carrier } = fragment;

        transporterName.push(...fragment.legs.map((leg) => leg.carrier.name));
        commissionAmount += (carrier.commissionEstimate / 100) * reservation.pricing.total;
      } else {
        const { line } = fragment;

        transporterName.push(line.name);
        commissionAmount += (line.commissionEstimate / 100) * reservation.pricing.total;
      }
    });
  }

  transporterName = transporterName.join(':');

  return {
    transporterName,
    allowsSeatSelection: hasSeatSelection,
    tripSlug,
    transportType: typeName,
    total: reservation.pricing.total,
    commission: commissionAmount,
    departure,
    originId: origin.id,
    originName: origin.name,
    originCityId: origin.cityId,
    originCityName: origin.cityName,
    originCountryName: origin.countryName,
    destinationId: destination.id,
    destinationName: destination.name,
    destinationCityId: destination.cityId,
    destinationCityName: destination.cityName,
    destinationCountryName: destination.countryName,
  };
}

function serializeWalletUsed(walletType, walletBalanceUsed) {
  if (!walletType || !walletBalanceUsed) return undefined;

  return walletType;
}

/**
 * Serializes the payment details for a purchase.
 *
 * @param {string} paymentType - The type of payment used.
 * @param {string} paymentEngine - The engine handling the payment.
 * @returns {string} The display name of the payment type based on the engine.
 */
function serializePaymentType(paymentType, paymentEngine) {
  const paymentDisplays = {
    conekta: 'oxxo',
    paycash: 'paycash',
    kushki: 'kushki',
  };

  return paymentType === 'store' ? paymentDisplays[paymentEngine] : paymentType;
}

function serializeEcommercePurchase(data) {
  const {
    couponCode,
    departs,
    email,
    insuranceAmount,
    markup,
    passengerSelection,
    phone,
    phoneCode,
    returns,
    redirect = false,
    redirectedFrom = '',
    roundTrip,
    taxes,
    token,
    total,
    walletType,
    walletBalanceUsed,
    paymentType,
    paymentEngine,
    isExchange,
    passengersQuantity,
  } = data;
  return {
    email,
    departs: serializeEcommerceReservation(departs),
    discountCode: couponCode,
    insuranceAmount,
    markup,
    passengerSelection,
    phone: `${phoneCode}${phone}`,
    redirect,
    redirectedFrom,
    returns: roundTrip ? serializeEcommerceReservation(returns) : null,
    taxes,
    token,
    total,
    walletUsed: serializeWalletUsed(walletType, walletBalanceUsed),
    walletAmount: walletBalanceUsed,
    paymentType: serializePaymentType(paymentType, paymentEngine),
    isExchange,
    passengersQuantity,
  };
}

function returnSeatSelectionStep(departs) {
  const trip = departs.fragments[0];
  const departureSeatSelection = trip.transportType === 'bus' && trip.line.allowsSeatSelection;
  let step = 2;

  if (departureSeatSelection) {
    step += 1;
  }

  return step;
}

/**
 * Function to check if the purchase has adjacent seats
 * @param {Object} departs
 * @param {Object} returns
 * @returns {boolean}
 */
function checkForAdjacentSeats(departs, returns) {
  const selectedSeatsDeparts = departs?.selectedSeats || [];
  const selectedSeatsReturns = returns?.selectedSeats || [];
  const selectedSeats = selectedSeatsDeparts.concat(selectedSeatsReturns);
  return selectedSeats.some((seat) => seat.isPickedAsAdjacent);
}

/**
 * Serialize carousel session state into a flat object for purchase events.
 * Only included when the carousel was actually used.
 * @param {object} carouselSession - carouselSession slice from Redux store.
 * @returns {object} Flat carousel props, or empty object if carousel was not used.
 */
export function serializeCarouselSession(carouselSession) {
  const departure = carouselSession.departure || {};
  const ret = carouselSession.return || {};
  const anyUsed = departure.used || ret.used;

  if (!anyUsed) return {};

  return {
    departureDaysCarouselDateSelected: departure.dateSelected ?? undefined,
    departureSearchOriginalDate: departure.originalSearchDate ?? undefined,
    returnDaysCarouselDateSelected: ret.dateSelected ?? undefined,
    returnSearchOriginalDate: ret.originalSearchDate ?? undefined,
    daysCarouselInteractionCount: (departure.interactionCount || 0) + (ret.interactionCount || 0),
  };
}

/**
 * Read carousel session from the Redux store and serialize it for purchase event tracking.
 * @returns {object} Flat carousel props.
 */
function getCarouselProps() {
  return serializeCarouselSession(store.getState().carouselSession || {});
}

/**
 * Read lodging-checkbox session from Redux and serialize for Mixpanel purchase events.
 * @returns {object} `{ lodging_checkbox_active }` when set, otherwise empty object.
 */
function getLodgingCheckboxProps() {
  const { active } = store.getState().lodgingCheckboxSession || {};
  return serializeLodgingCheckboxProps(active);
}

/**
 * Serialize the purchase data for tracking.
 * @param {Object} purchase - The purchase object.
 * @returns {Object} The serialized purchase data.
 */
function serializePurchase(purchase) {
  const {
    token,
    total,
    taxes,
    markup,
    insuranceAmount,
    international,
    passengerSelection,
    departs,
    returns,
    openTicket,
    couponCode,
    redirect = false,
    redirectedFrom = '',
    recommendedTripType,
    useRecommendedTrip,
    preSelectedSeats,
    bulkTicket,
    passengers,
  } = purchase;

  // Get user and holderEditingEnabled from Redux store
  const state = store.getState();
  const { user } = state.siemprePlus?.toJS() || {};

  // Determine if first passenger is the account holder
  const isAccountHolderValue = isAccountHolder({
    purchase: { passengers },
    user,
  });

  return {
    token,
    pricing: {
      total,
      taxes,
      markup,
      insuranceAmount,
    },
    international,
    passengerSelection,
    departs: serializeReservation(departs),
    returns: serializeReservation(returns),
    openTicket,
    discountCode: couponCode,
    lang: CurrentLang({ shouldReadDefaultLanguageFromStore: false }),
    redirect,
    redirectedFrom,
    isUsingAdjacentSeats: checkForAdjacentSeats(departs, returns),
    recommendedTripType,
    useRecommendedTrip,
    resultSeats: Boolean(preSelectedSeats),
    bulkTicket: Boolean(bulkTicket),
    ...(isAccountHolderValue !== undefined && { is_account_holder: isAccountHolderValue }),
    ...getCarouselProps(),
  };
}

/**
 * Mixpanel extra props for lodging-checkbox state. Must live in `extraProps` (5th arg
 * to trackPurchaseEvent), not in serializePurchase — user-analytics re-serializes
 * the purchase payload and strips unknown keys (same pattern as segments_occupancy).
 * @returns {object} `{ lodging_checkbox_active }` when set, otherwise empty object.
 */
function buildLodgingCheckboxExtraProps() {
  return getLodgingCheckboxProps();
}

/**
 * Mixpanel extra props for the CDS Frequent Passenger opt-in checkbox state. Must live in
 * `extraProps` (5th arg to trackPurchaseEvent), not in serializePurchase — user-analytics
 * re-serializes the purchase payload and strips unknown keys (same pattern as lodging checkbox).
 * @param {Object} purchase - The purchase object.
 * @returns {object} `{ cds_frequent_passenger_opt_in }` when set, otherwise empty object.
 */
function buildCdsFrequentPassengerOptInExtraProps(purchase) {
  return serializeCdsFrequentPassengerOptInProps(purchase);
}

/**
 * Builds the extra event props carrying per-segment bus occupancy. Returned as
 * an object so it can be spread into `extraProps` only when data is available,
 * keeping existing event payloads untouched otherwise.
 * @param {Object} purchase - The purchase object.
 * @returns {Object} Either `{ segments_occupancy }` or an empty object.
 */
function buildOccupancyExtraProps(purchase) {
  const segmentsOccupancy = getSegmentsOccupancy(purchase);
  return segmentsOccupancy ? { segments_occupancy: segmentsOccupancy } : {};
}

/**
 * Tracks the purchase attempt event.
 * @param {Object} purchase - The purchase object.
 */
export function purchaseAttemptTracker(purchase) {
  ensureRnMixpanelIdentified();

  const { env } = store.getState().whitelabelConfig;
  const rnNativeEventProps = getRnMixpanelNativeEventProps();
  const occupancyProps = buildOccupancyExtraProps(purchase);
  const extraProps = {
    ...rnNativeEventProps,
    ...occupancyProps,
    ...buildLodgingCheckboxExtraProps(),
    ...buildCdsFrequentPassengerOptInExtraProps(purchase),
  };
  if (env.taplytics.enabled) {
    const taplytics = getInstance();
    taplytics.track('Purchase Attempt', { ...serializePurchase(purchase), ...extraProps });
  }
  const queryParams = new URLSearchParams(window?.location.search);
  if (queryParams.has('redirected_to')) {
    const analyticsData = {
      ...serializePurchaseForTracking(serializePurchase(purchase)),
      'Email Type': queryParams.get('redirected_to'),
    };
    queryParams.delete('redirected_to');
    window?.history.replaceState({}, null, removeURLParam('redirected_to', window?.location.href));
    trackEvent('Email Link Clicked', analyticsData);
  }
  trackPurchaseEvent(
    'Purchase Attempt',
    serializePurchase(purchase),
    undefined,
    undefined,
    extraProps,
  );
  ecTracker.trackAddToCart(serializeEcommercePurchase(purchase));
}

export function exchangeAttemptTracker(purchase) {
  trackPurchaseEvent('Exchange Attempt', serializePurchase(purchase));
  ecTracker.trackAddToCart(serializeEcommercePurchase(purchase));
}

/**
 * Track the "Passengers Created" funnel event.
 *
 * @param {Object} purchase - Serialized purchase payload (includes `selectedFrequentPassengers` when passed from Passengers).
 */
export function passengersTracker(purchase) {
  const { selectedFrequentPassengers, departs, returns } = purchase;

  const selectedSeatsDeparts = departs?.selectedSeats || [];
  const selectedSeatsReturns = returns?.selectedSeats || [];
  const allSelectedSeats = selectedSeatsDeparts.concat(selectedSeatsReturns);

  const seats = allSelectedSeats.map((seat) => {
    const seatLevel =
      seat.seatLevel != null && !Number.isNaN(Number(seat.seatLevel))
        ? Number(seat.seatLevel)
        : undefined;

    return {
      number: seat.number ?? undefined,
      floor: seat.seatFloor ?? seatLevel,
    };
  });

  const extraProps = {
    'Selected Frequent Passengers': selectedFrequentPassengers.map((passenger) => ({
      firstName: passenger.firstName,
      lastName: passenger.lastName,
      email: passenger.email,
    })),
    'Selected Frequent Passengers Count': selectedFrequentPassengers.length,
    'Seats': seats,
    ...buildOccupancyExtraProps(purchase),
    ...buildLodgingCheckboxExtraProps(),
    ...buildCdsFrequentPassengerOptInExtraProps(purchase),
  };
  trackPurchaseEvent(
    'Passengers Created',
    serializePurchase(purchase),
    undefined,
    undefined,
    extraProps,
  );
  ecTracker.trackPurchaseFunnel(1, serializeEcommercePurchase(purchase));
}

/**
 * Track the "Exchange Passengers Created" event.
 *
 * @param {Object} purchase - Purchase payload for exchange flow.
 */
export function exchangePassengersTracker(purchase) {
  trackPurchaseEvent(
    'Exchange Passengers Created',
    serializePurchase(purchase),
    undefined,
    undefined,
    undefined,
  );
  ecTracker.trackPurchaseFunnel(1, serializeEcommercePurchase(purchase));
}

export function seatChangeTracker(purchase = {}, way, fromView) {
  if (fromView === 'results') {
    trackEvent('Seat Change', {
      Way: way,
      View: fromView,
    });
    return;
  }

  const serializedPurchase = serializeEcommercePurchase(purchase);
  const { departs, returns } = purchase;

  if (way === 'departure') {
    // segment
    trackReservationEvent('Seat Change', serializeReservation(departs, 'departs'));
    // GA
    if (departs.transportType === 'bus' && departs.fragments[0].line.allowsSeatSelection) {
      ecTracker.trackPurchaseFunnel(2, serializedPurchase);
    }
  } else {
    // segment
    trackReservationEvent('Seat Change', serializeReservation(returns, 'returns'));
    // GA
    if (returns.transportType === 'bus' && returns.fragments[0].line.allowsSeatSelection) {
      ecTracker.trackPurchaseFunnel(returnSeatSelectionStep(departs), serializedPurchase);
    }
  }
}

export function exchangeSeatChangeTracker(purchase, way, fromView) {
  if (fromView === 'results') {
    trackEvent('Exchange Seat Change', {
      Way: way,
      View: fromView,
    });
    return;
  }

  const { departs, returns } = purchase;
  const serializedPurchase = serializeEcommercePurchase(purchase);

  if (way === 'departure' && departs) {
    // segment
    trackReservationEvent('Exchange Seat Change', serializeReservation(departs, 'departs'));
    // GA - only if we have valid ecommerce data
    if (
      serializedPurchase &&
      departs.transportType === 'bus' &&
      departs.fragments?.[0]?.line?.allowsSeatSelection
    ) {
      ecTracker.trackPurchaseFunnel(2, serializedPurchase);
    }
  } else if (way === 'return' && returns) {
    // segment
    trackReservationEvent('Exchange Seat Change', serializeReservation(returns, 'returns'));
    // GA - only if we have valid ecommerce data
    if (
      serializedPurchase &&
      returns.transportType === 'bus' &&
      returns.fragments?.[0]?.line?.allowsSeatSelection
    ) {
      ecTracker.trackPurchaseFunnel(returnSeatSelectionStep(departs), serializedPurchase);
    }
  }
}

export function confirmItineraryTracker(purchase) {
  trackPurchaseEvent('Confirm Itinerary', serializePurchase(purchase), undefined, undefined, {
    ...buildOccupancyExtraProps(purchase),
    ...buildLodgingCheckboxExtraProps(),
    ...buildCdsFrequentPassengerOptInExtraProps(purchase),
  });
}

/**
 * Tracks the `BookingReview Details Clicked` event, fired when the user opens
 * the trip details modal from the booking review. Enriched with
 * `segments_occupancy` so the open interaction carries the same bus capacity
 * signal as `Purchase Attempt`, `Passengers Created` and `Confirm Itinerary`.
 *
 * @param {Object} purchase - The purchase object from the store.
 */
export function bookingReviewDetailsClickedTracker(purchase) {
  trackEvent(CHECKOUT_DETAILS_CLICKED, buildOccupancyExtraProps(purchase));
}

export function exchangeConfirmItineraryTracker(purchase) {
  trackPurchaseEvent('Exchange Confirm Itinerary', serializePurchase(purchase));
}

export function purchaseOnHoldTracker(token, callback) {
  gaTracker.trackPurchaseOnHold(token, callback);
}

export function purchaseTracker(
  { purchase, funnelStep, cardBrand, paymentType, paymentEngine, isExchange, passengersQuantity },
  callback,
) {
  const serializedPurchase = serializeEcommercePurchase({
    ...purchase,
    paymentType,
    paymentEngine,
    isExchange,
    passengersQuantity,
  });

  ecTracker.trackPurchase(serializedPurchase);
  ecTracker.trackPurchaseFunnel(funnelStep, serializedPurchase, cardBrand, callback);
}

/**
 * Event to track when the user click on the return CTA offer reminder
 * The return CTA is present in passengers and checkout pages if ft flag is enabled
 */
export function returnCTATracker(buttonVariant) {
  trackEvent('Return Reminder Clicked', {
    Button: buttonVariant,
  });
}

/**
 * Tracks actions in the price change modal.
 * @param {string} action - Action performed in the modal:
 * - "View Results": Click to return to schedules
 * - "Continue": Click to continue with purchase
 */
export function priceChangeModalTracker(action) {
  trackEvent('Price Change Modal', {
    Action: action,
  });
}

/**
 * Tracks when user selects the quick purchase option.
 * @param {string} from - Origin of the selection: 'schedule' or 'seats'.
 * @param {boolean} isRecommended - Whether the trip is a recommended trip.
 */
export function quickPurchaseSelectedTracker(from, isRecommended) {
  trackEvent('Quick purchase selected', {
    'From': from,
    'Is Recommended': isRecommended,
  });
}

/**
 * Tracks when user clicks back to search from QuickCheckout.
 */
export function quickPurchaseBackToSearchTracker() {
  trackEvent('Quick purchase back to search');
}

/**
 * Tracks when user clicks edit passengers from QuickCheckout.
 */
export function quickPurchasePassengerEditTracker() {
  trackEvent('Quick purchase passenger edit');
}

/**
 * Tracks when user clicks edit seats from QuickCheckout.
 */
export function quickPurchaseSeatsEditTracker() {
  trackEvent('Quick purchase seats edit');
}

/**
 * Tracks when user clicks edit buyer data from QuickCheckout.
 */
export function quickPurchaseBuyerEditTracker() {
  trackEvent('Quick purchase buyer edit');
}

/**
 * Tracks when user clicks the main purchase button from QuickCheckout.
 */
export function quickPurchaseMainButtonTracker() {
  trackEvent('Quick purchase main button');
}

/**
 * Tracks seat-selection confirmation and includes selected zone metadata when available.
 * @param {object} params - way, selectedSeats, seatPriceZones.
 * @param {string} params.way - Trip way ('departure' or 'return').
 * @param {Array<object>} params.selectedSeats - Currently selected seats.
 * @param {Array<object>} params.seatPriceZones - Resolved seat price zones for current trip.
 */
export function continueWithSeatsTracker({ way, selectedSeats = [], seatPriceZones = [] }) {
  const selectedPassengerSeats = selectedSeats.filter((seat) => !seat.isPickedAsAdjacent);
  const hasZones = Array.isArray(seatPriceZones) && seatPriceZones.length > 0;

  if (!hasZones || selectedPassengerSeats.length === 0) {
    trackEvent(CONTINUE_WITH_SEATS, { way });
    return;
  }

  const zoneMap = buildZoneMap({ seatPriceZones });
  const zoneLabelsSet = new Set();

  selectedPassengerSeats.forEach((seat) => {
    const { label } = getSeatZone(seat, zoneMap);
    if (label) zoneLabelsSet.add(label);
  });

  trackEvent(CONTINUE_WITH_SEATS, {
    way,
    seatSelection: {
      zones: Array.from(zoneLabelsSet),
      zonesCount: zoneLabelsSet.size,
    },
  });
}

/**
 * Tracks when a quick purchase is being created (attempt).
 */
export function quickPurchaseAttemptTracker() {
  trackEvent('Quick purchase attempt');
}

/**
 * Tracks the Quick Purchase Status event.
 * Fires once per search results view after the GrowthBook eligibility attribute is applied.
 * @param {object} params - Event parameters.
 * @param {boolean} params.eligible - Whether the user matches Quick Purchase eligibility criteria (independent of experiment).
 * @param {boolean} params.shown - Whether Quick Purchase was shown to the user (experiment variant is on).
 * @param {string} [params.reason] - Reason Quick Purchase was not shown (only when shown=false).
 * @param {number} params.passengersCount - Number of available passengers.
 * @param {string} params.passengersSource - Source: 'frequent' | 'local_storage' | 'none'.
 */
export function quickPurchaseStatusTracker({
  eligible,
  shown,
  reason,
  passengersCount,
  passengersSource,
}) {
  const properties = {
    'Eligible': eligible,
    'Shown': shown,
    'Passengers Count': passengersCount,
    'Passengers Source': passengersSource,
  };
  if (!shown && reason) {
    properties.Reason = reason;
  }
  trackEvent('Quick Purchase Status', properties);
}

/**
 * Tracks when the user arrives at the quick-checkout screen.
 */
export function quickPurchaseViewTracker() {
  trackEvent('Quick purchase view');
}

/**
 * Tracks when Quick Purchase opens with incomplete buyer/passenger data, i.e. one or more
 * tenant-required fields are missing/invalid and the user must complete them before paying
 * (CON-1076). The missing fields are keyed as `passenger_<n>.<field>` / `buyer.<field>`.
 * @param {string[]} missingFields - Detected missing or invalid required fields.
 */
export function quickPurchaseIncompleteDataTracker(missingFields = []) {
  trackEvent('Incomplete data for Quick Purchase detected', {
    'Missing Fields': missingFields,
    'Missing Fields Count': missingFields.length,
  });
}

/**
 * Tracks every Quick Purchase payment attempt with the buyer/passenger data the purchase holds.
 *
 * Fires on complete attempts too: the incomplete ones are only meaningful as a share of all
 * attempts. Unlike `quickPurchaseIncompleteDataTracker`, this carries the purchase token — trackEvent
 * attaches no super properties, so without it an event cannot be traced back to a purchase.
 *
 * Field lists use the same `buyer.<field>` / `passenger_<n>.<field>` identifiers as the
 * incomplete-data event, so both can be analysed together.
 * @param {object} params - Tracking parameters.
 * @param {string} params.token - Purchase token.
 * @param {string[]} params.missingFields - Required fields absent from the purchase.
 * @param {string[]} params.unsavedBuyerFields - Buyer fields filled in the form but absent from the purchase.
 * @param {object} params.buyer - Buyer data held by the purchase.
 * @param {object} params.buyerForm - Buyer data held by the form.
 * @param {Array<object>} params.passengers - Passenger data held by the purchase.
 * @param {string} params.paymentOption - Selected payment option.
 */
export function quickPurchasePaymentAttemptTracker({
  token,
  missingFields = [],
  unsavedBuyerFields = [],
  buyer = {},
  buyerForm = {},
  passengers = [],
  paymentOption,
}) {
  trackEvent('Quick Purchase Payment Attempt', {
    'Token': token,
    'Payment Option': paymentOption,
    'Missing Fields': missingFields,
    'Missing Fields Count': missingFields.length,
    'Unsaved Buyer Fields': unsavedBuyerFields,
    'Has Incomplete Data': missingFields.length > 0 || unsavedBuyerFields.length > 0,
    'Buyer': buyer,
    'Buyer Form': buyerForm,
    'Passengers': passengers,
    'Passengers Count': passengers.length,
  });
}

/**
 * Tracks click on the Tapatio Tour banner CTA from purchase complete.
 * @param {object} params - Tracking parameters.
 * @param {string} [params.token] - Purchase token.
 * @param {string} [params.brand] - Whitelabel brand.
 * @param {string} [params.url] - Full banner URL opened on click (carries the brand ref).
 * @param {string} [params.origin] - Trip origin city name.
 * @param {string} [params.destination] - Trip destination city name.
 * @param {string} [params.context] - UI context where click happened.
 */
export function tapatioBannerClickTracker({
  token,
  brand,
  url,
  origin,
  destination,
  context = 'purchase_complete',
}) {
  trackEvent('Tapatio Tour Banner Clicked', {
    'Purchase Token': token,
    'Brand': brand,
    'Banner URL': url,
    'Origin': origin,
    'Destination': destination,
    'Context': context,
  });
}
